// Anfragen/Bestellungen: Gemeldet (aus „Bedarf an Einkauf melden") + Loxpal + weitere
// Kategorien, die zuvor nur in Jasmins Excel geführt wurden ("Anfragen Sonstiges").
// Aufgebaut wie der Blechbedarf: Zeile anklicken → bearbeiten, Stornieren mit Grund,
// Papierkorb (Wiederherstellen nur Admin/Meister), Verlauf (wer hat wann was geändert).
import { useEffect, useState } from 'react';
import { Ban, Camera, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import {
  api, type EinkaufAufgabe, type EinkaufSonstigesEintrag, type EinkaufSonstigesKategorie, type SonstigesBild,
} from '../lib/api';
import { useEntwurf, useEntwurfKey, istNichtLeer } from '../hooks/useEntwurf';
import { EntwurfBanner } from '../components/EntwurfBanner';
import { ErrorBanner } from '../components/ErrorBanner';
import { NeuEtikett, useEinkaufNeu } from '../components/EinkaufNeu';
import { PapierkorbModal, StornoDialog, VerlaufListe } from '../components/StornoPapierkorb';
import { AnfrageModal, BestellModal, ZEUGNIS_HINWEIS } from './SonstigesAktionen';
import { ListeEinfuegenModal } from './SonstigesListe';
import { parseMehrzeilig } from '../lib/sonstigesListe';

type Tab = 'gemeldet' | EinkaufSonstigesKategorie;

const KATEGORIE_TABS: { key: EinkaufSonstigesKategorie; label: string }[] = [
  { key: 'loxpal',      label: 'Loxpal' },
  { key: 'sonstiges',   label: 'Sonstiges' },
  { key: 'projekte',    label: 'Projekte' },
  { key: 'schietinger', label: 'Schietinger' },
  { key: 'versand',     label: 'Versand' },
  { key: 'wuerth_flex', label: 'Würth Flex' },
  { key: 'farbe',       label: 'Farbe' },
  { key: 'schweissen',  label: 'Schweißen' },
];

const tabAusUrl = (): Tab => {
  const t = new URLSearchParams(window.location.search).get('tab');
  return t === 'gemeldet' || KATEGORIE_TABS.some(k => k.key === t) ? (t as Tab) : 'loxpal';
};

const heute = () => new Date().toISOString().slice(0, 10);
const datumDe = (d: string | null | undefined) => (d ? new Date(d).toLocaleDateString('de-DE') : '—');

const CHIP = 'inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap';
const FILTER = (aktiv: boolean) =>
  `px-3 py-1 rounded-full text-xs font-medium border ${aktiv ? 'bg-brand text-white border-brand' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`;

export function EinkaufSonstiges() {
  const [tab, setTab] = useState<Tab>(tabAusUrl);
  const me = useApi(() => api.dashboard.me(), []);
  const darfWiederherstellen = me.data?.user?.role === 'admin' || me.data?.user?.role === 'meister';
  // Gemeldete Bedarfe (alle Status) — die Zahl am Reiter zählt nur die offenen
  const meldungen = useApi(() => api.einkaufAufgaben.list(true, 'neu'), []);
  const offeneMeldungen = (meldungen.data?.data ?? []).filter(a => a.status === 'offen').length;

  return (
    <div className="p-8">
      <header className="mb-4">
        <div className="text-xs uppercase tracking-wider text-slate-500">Einkauf</div>
        <h1 className="text-2xl font-bold text-slate-900">Anfragen &amp; Bestellungen</h1>
      </header>

      <div className="flex gap-1 border border-slate-200 bg-white rounded-lg p-1 mb-4 flex-wrap w-fit">
        <button onClick={() => setTab('gemeldet')}
                className={`px-3 py-1.5 rounded text-sm font-medium whitespace-nowrap ${
                  tab === 'gemeldet' ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
          Gemeldet
          {offeneMeldungen > 0 && (
            <span className="ml-1.5 rounded-full bg-amber-400 text-amber-950 text-[11px] font-bold px-1.5 py-0.5">{offeneMeldungen}</span>
          )}
        </button>
        <span className="w-px bg-slate-200 mx-1" />
        {KATEGORIE_TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-3 py-1.5 rounded text-sm font-medium whitespace-nowrap ${
                    tab === t.key ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'gemeldet'
        ? <GemeldetListe key="gemeldet" liste={meldungen} darfWiederherstellen={darfWiederherstellen} />
        : <KategorieListe key={tab} tab={tab} darfWiederherstellen={darfWiederherstellen} />}
    </div>
  );
}

/* ───────────────────────── Kategorien (Loxpal … Schweißen) ───────────────────────── */

type Entwurf = Partial<EinkaufSonstigesEintrag>;

const VERLAUF_LABEL: Record<string, string> = {
  kategorie: 'Kategorie', datum: 'Datum', stueck: 'Stück', was: 'Was', kg: 'Kg', kommentar: 'Notiz',
  angefragt_bei: 'Angefragt bei', bestellt_bei: 'Bestellt bei', liefertermin: 'Liefertermin', preis: 'Preis',
  einheit: 'Einheit', ab: 'AB', rechnungsnummer: 'Rechnungsnr.', referenz: 'Referenz', geliefert_am: 'Wareneingang',
};

type KStatus = 'offen' | 'angefragt' | 'bestellt' | 'geliefert';
// Status ist abgeleitet: „Wareneingang" gesetzt = geliefert, sonst „Bestellt bei" = bestellt, sonst „Angefragt bei" = angefragt
export type SFilter = 'unbestellt' | 'offen' | 'angefragt' | 'bestellt' | 'geliefert' | 'alle';
export const SFILTER: [SFilter, string][] = [
  ['unbestellt', 'Unbestellt'], ['offen', 'Offen'], ['angefragt', 'Angefragt'],
  ['bestellt', 'Bestellt'], ['geliefert', 'Angekommen'], ['alle', 'Alle'],
];
export function passtZuFilter(r: EinkaufSonstigesEintrag, f: SFilter, suche: boolean): boolean {
  const st = statusVon(r);
  if (f === 'alle') return suche || st !== 'angefragt';
  if (f === 'unbestellt') return st === 'offen' || (suche && st === 'angefragt');
  return st === f;
}
export const statusVon = (r: EinkaufSonstigesEintrag): KStatus =>
  r.geliefert_am?.trim() ? 'geliefert' : r.bestellt_bei?.trim() ? 'bestellt' : r.angefragt_bei?.trim() ? 'angefragt' : 'offen';
const KSTATUS_CHIP: Record<KStatus, { label: string; cls: string }> = {
  offen:     { label: 'Offen',     cls: 'bg-amber-100 text-amber-800' },
  angefragt: { label: 'Angefragt', cls: 'bg-teal-100 text-teal-800' },
  bestellt:  { label: 'Bestellt',  cls: 'bg-sky-100 text-sky-800' },
  geliefert: { label: 'Geliefert', cls: 'bg-emerald-100 text-emerald-800' },
};

function KategorieListe({ tab, darfWiederherstellen }: { tab: EinkaufSonstigesKategorie; darfWiederherstellen: boolean }) {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<SFilter>('unbestellt');
  const [modal, setModal] = useState<Entwurf | null>(null);
  const [storno, setStorno] = useState<{ id: number; titel: string } | null>(null);
  const [korb, setKorb] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const [verlaufOffen, setVerlaufOffen] = useState(false);
  // Markierung für Sammelaktionen (Anfrage / Bestellung / Wareneingang / Storno)
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [aktion, setAktion] = useState<'anfrage' | 'bestellen' | 'storno' | null>(null);
  const [listeOffen, setListeOffen] = useState(false);
  // „Neuer Eintrag": weitere Positionen, die gleichzeitig angelegt werden
  const [weitere, setWeitere] = useState<{ stueck: string; was: string; preis?: string; einheit?: string }[]>([]);
  const [bilderTick, setBilderTick] = useState(0);
  const [neueBilder, setNeueBilder] = useState<File[]>([]);        // beim Anlegen vorgemerkt, Upload nach dem Speichern
  const bilderIdx = useApi(() => api.einkaufSonstiges.bilderIndex(), []);
  const lieferantenListe = useApi(() => api.lieferanten.list(), []);
  const lieferanten = lieferantenListe.data?.data ?? [];

  // Entwurf (Autosave) — je Reiter ein eigener Entwurf; nur beim Anlegen, nicht beim Bearbeiten
  const ent = useEntwurf<Entwurf>(
    useEntwurfKey(`anfragen-${tab}`),
    modal && !modal.id ? modal : null,
    v => istNichtLeer({ ...v, datum: undefined }),
  );

  const list = useApi(() => api.einkaufSonstiges.list(tab, q || undefined), [tab, q]);
  const alle = list.data?.data ?? [];
  // „NEU“ (nur für Einkauf-Empfänger): von anderen erfasst, noch nicht angeklickt
  const { daten: neuDaten, gesehen: neuGesehen } = useEinkaufNeu();
  const neuIds = new Set(neuDaten.sonstiges.map(s => s.id));
  // Wie im Blechbedarf: Angefragte stehen nur im Reiter „Angefragt“ — nicht unter „Unbestellt“/„Alle“ (außer bei aktiver Suche)
  const rows = alle.filter(r => passtZuFilter(r, filter, !!q.trim()));
  const zaehl = (f: SFilter) => alle.filter(r => passtZuFilter(r, f, !!q.trim())).length;
  const zeigtKg = tab === 'farbe';
  const zeigtReferenz = tab === 'schweissen';
  const spalten = 14 + (zeigtKg ? 1 : 0) + (zeigtReferenz ? 1 : 0);
  const gewaehlt = alle.filter(r => sel.has(r.id));
  const fuerAnfrage = gewaehlt.filter(r => statusVon(r) === 'offen' || statusVon(r) === 'angefragt');
  const fuerBestellung = gewaehlt.filter(r => statusVon(r) !== 'bestellt' && statusVon(r) !== 'geliefert');
  const fuerWareneingang = gewaehlt.filter(r => statusVon(r) === 'bestellt');
  const toggle = (id: number) => setSel(x => { const n = new Set(x); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const alleSichtbarGewaehlt = rows.length > 0 && rows.every(r => sel.has(r.id));
  const nachAktion = () => { setSel(new Set()); list.reload(); };

  const set = (k: keyof EinkaufSonstigesEintrag, v: string | number | null) => setModal(m => ({ ...m, [k]: v }));
  const oeffneNeu = () => {
    const start: Entwurf = { datum: heute() };
    setFehler(''); setVerlaufOffen(false); setWeitere([]); setNeueBilder([]); setModal(start);
    ent.beimOeffnen({});
  };
  const oeffneBearbeiten = (r: EinkaufSonstigesEintrag) => { setFehler(''); setVerlaufOffen(false); setNeueBilder([]); setModal({ ...r }); };
  const schliesse = () => { setModal(null); setFehler(''); setWeitere([]); setNeueBilder([]); };

  // „Was“ mehrzeilig (Liste aus Teams/Excel einfügen oder Enter): jede Zeile wird ein eigener Eintrag.
  // Erste Zeile bleibt im Formular, die übrigen landen als „Weitere Positionen“ (mit Stück/Preis/Einheit).
  const wasAendern = (wert: string) => {
    if (modal?.id || !/[\r\n]/.test(wert)) { set('was', wert); return; }
    const pos = parseMehrzeilig(wert);
    if (pos.length === 0) { set('was', wert.replace(/[\r\n]+/g, ' ').trim() === '' ? '' : wert.replace(/[\r\n]+/g, ' ')); return; }
    const [erste, ...rest] = pos;
    setModal(m => ({
      ...m,
      was: erste!.was,
      stueck: erste!.stueck || m?.stueck || '',
      ...(erste!.preis ? { preis: erste!.preis, einheit: erste!.einheit || m?.einheit || '' } : {}),
    }));
    if (rest.length) setWeitere(l => [...l.filter(x => x.was.trim() || x.stueck.trim()), ...rest]);
  };

  const speichern = async () => {
    if (!modal?.was?.trim()) return;
    setBusy(true); setFehler('');
    try {
      const felder = {
        datum: modal.datum ?? null, stueck: modal.stueck ?? null, was: modal.was.trim(), kg: modal.kg ?? null,
        kommentar: modal.kommentar ?? null, angefragt_bei: modal.angefragt_bei ?? null, bestellt_bei: modal.bestellt_bei ?? null,
        liefertermin: modal.liefertermin ?? null, preis: modal.preis ?? null, einheit: modal.einheit ?? null,
        ab: modal.ab ?? null, rechnungsnummer: modal.rechnungsnummer ?? null, referenz: modal.referenz ?? null,
        geliefert_am: modal.geliefert_am ?? null,
      };
      if (modal.id) await api.einkaufSonstiges.update(modal.id, felder);
      else {
        const angelegt = await api.einkaufSonstiges.create({ ...felder, kategorie: tab });
        // Bilder gehören zum ersten Eintrag; schlägt der Upload fehl, ist der Eintrag trotzdem gespeichert
        const neueId = angelegt?.data?.id;
        if (neueBilder.length && neueId) {
          try { await api.einkaufSonstiges.bilderHochladen(neueId, neueBilder); bilderIdx.reload(); }
          catch (e) { alert(`Eintrag gespeichert, aber Bild-Upload fehlgeschlagen: ${(e as Error).message}\nBitte im Eintrag erneut hochladen.`); }
        }
        // weitere Positionen: gleiche Kopfdaten (Datum, Referenz, Anfrage bei …), eigene Menge/Beschreibung
        for (const w of weitere.filter(x => x.was.trim())) {
          await api.einkaufSonstiges.create({ ...felder, stueck: w.stueck.trim() || null, was: w.was.trim(), kategorie: tab,
            ...(w.preis !== undefined ? { preis: w.preis.trim() || null, einheit: (w.einheit ?? '').trim() || null } : {}) });
        }
        ent.nachSpeichern();
      }
      setModal(null);
      list.reload();
    } catch (e) {
      setFehler((e as Error).message);
      if ((e as { status?: number }).status === 404) list.reload();
    }
    setBusy(false);
  };

  const stornieren = async (grund: string) => {
    if (!storno) return;
    await api.einkaufSonstiges.storno(storno.id, grund);
    setStorno(null); setModal(null); list.reload();
  };
  // Sammel-Storno: ein Grund für alle markierten Einträge
  const sammelStornieren = async (grund: string) => {
    const fehlgeschlagen: number[] = [];
    for (const r of gewaehlt) {
      try { await api.einkaufSonstiges.storno(r.id, grund); } catch { fehlgeschlagen.push(r.id); }
    }
    setSel(new Set(fehlgeschlagen)); list.reload();
    if (fehlgeschlagen.length === 0) setAktion(null);
    else throw new Error(`${fehlgeschlagen.length} Eintrag/Einträge konnten nicht storniert werden (evtl. schon storniert).`);
  };
  const wareneingang = async () => {
    if (fuerWareneingang.length === 0) return;
    if (!confirm(`Wareneingang für ${fuerWareneingang.length} Position${fuerWareneingang.length === 1 ? '' : 'en'} heute (${datumDe(heute())}) buchen?`)) return;
    setBusy(true); setFehler('');
    try {
      for (const r of fuerWareneingang) await api.einkaufSonstiges.update(r.id, { geliefert_am: heute() });
      nachAktion();
    } catch (e) { setFehler((e as Error).message); list.reload(); }
    setBusy(false);
  };

  return (
    <>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <input placeholder="🔍 Suche …" value={q} onChange={e => setQ(e.target.value)} className="input min-w-[240px] max-w-[280px]" />
          <div className="flex gap-1.5 flex-wrap">
            {SFILTER.map(([f, label]) => (
              <button key={f} className={FILTER(filter === f)} data-testid={`sfilter-${f}`} onClick={() => setFilter(f)}>{label} ({zaehl(f)})</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setKorb(true)} className="btn-secondary inline-flex items-center gap-1.5 whitespace-nowrap">
            <Trash2 className="w-4 h-4" /> Papierkorb
          </button>
          <button onClick={() => setListeOffen(true)} data-testid="liste-einfuegen"
                  className="border border-slate-300 bg-white text-slate-700 rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-slate-50 whitespace-nowrap"
                  title="Mehrere Positionen auf einmal: Liste einfügen → Vorschau → alle anlegen">📋 Liste einfügen</button>
          <button onClick={oeffneNeu} className="bg-brand text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:opacity-90 whitespace-nowrap inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Neuer Eintrag
            {ent.entwurf && !modal && (
              <span className="ml-1 rounded-full bg-amber-300 text-amber-950 text-[11px] font-semibold px-2 py-0.5"
                    title="Du hast einen nicht gespeicherten Entwurf — er liegt nur in diesem Browser">📝 Entwurf</span>
            )}
          </button>
        </div>
      </div>

      {list.error && <div className="mb-3"><ErrorBanner error={list.error} onRetry={list.reload} /></div>}
      {fehler && !modal && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{fehler}</div>}

      {gewaehlt.length > 0 && (
        <div className="mb-3 flex items-center gap-2 flex-wrap bg-sky-50 border border-sky-200 rounded-lg px-4 py-2" data-testid="so-aktionsleiste">
          <span className="text-sm font-semibold text-sky-900">{gewaehlt.length} markiert</span>
          <button className="btn-secondary" disabled={fuerAnfrage.length === 0} onClick={() => setAktion('anfrage')} data-testid="so-anfrage"
                  title="Anfrage an einen oder mehrere Lieferanten (Outlook-Entwurf)">✉ Anfrage ({fuerAnfrage.length})</button>
          <button className="btn-secondary" disabled={fuerBestellung.length === 0} onClick={() => setAktion('bestellen')} data-testid="so-bestellen"
                  title="Bestellung als PDF">📦 Bestellen ({fuerBestellung.length})</button>
          <button className="btn-secondary" disabled={busy || fuerWareneingang.length === 0} onClick={() => void wareneingang()} data-testid="so-wareneingang"
                  title="Ware ist angekommen">📥 Wareneingang ({fuerWareneingang.length})</button>
          <button className="inline-flex items-center gap-1.5 border border-red-200 text-red-700 bg-white rounded-lg px-3 py-1.5 text-sm hover:bg-red-50"
                  onClick={() => setAktion('storno')} data-testid="so-storno"><Ban className="w-4 h-4" /> Stornieren ({gewaehlt.length})</button>
          <button className="ml-auto text-sm text-slate-500 hover:underline" onClick={() => setSel(new Set())}>Auswahl aufheben</button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
        <table className="w-full text-[13px] min-w-[1100px]">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600 uppercase">
            <tr>
              <th className="px-2.5 py-1.5 w-8">
                <input type="checkbox" aria-label="Alle sichtbaren Einträge markieren" checked={alleSichtbarGewaehlt}
                       onChange={e => setSel(e.target.checked ? new Set(rows.map(r => r.id)) : new Set())} />
              </th>
              {['Datum', 'Stück', 'Was', ...(zeigtKg ? ['Kg'] : []), ...(zeigtReferenz ? ['Referenz'] : []), 'Notiz',
                'Angefragt bei', 'Bestellt bei', 'Liefertermin', 'Preis', 'Einheit', 'AB', 'Wareneingang', 'Status'].map(h => (
                <th key={h} className="text-left px-2.5 py-1.5 font-medium whitespace-nowrap">{h}</th>
              ))}
              <th className="w-8" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.loading && <tr><td colSpan={spalten} className="text-center py-10 text-slate-500">Lädt …</td></tr>}
            {!list.loading && !list.error && alle.length === 0 && (
              <tr><td colSpan={spalten} className="text-center py-10 text-slate-500">{q ? 'Keine Treffer' : 'Keine Einträge'}</td></tr>
            )}
            {!list.loading && alle.length > 0 && rows.length === 0 && (
              <tr><td colSpan={spalten} className="text-center py-10 text-slate-500">
                Keine Einträge mit diesem Status — <button className="underline" onClick={() => setFilter('alle')}>Alle anzeigen</button>
              </td></tr>
            )}
            {rows.map(r => {
              const st = KSTATUS_CHIP[statusVon(r)];
              return (
                <tr key={r.id} onClick={() => { oeffneBearbeiten(r); if (neuIds.has(r.id)) neuGesehen('sonstiges', [r.id]); }} className={`hover:bg-slate-50 cursor-pointer ${sel.has(r.id) ? 'bg-sky-50/60' : ''}`}>
                  <td className="px-2.5 py-1.5" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" aria-label={`Eintrag ${r.id} markieren`} checked={sel.has(r.id)} onChange={() => toggle(r.id)} />
                  </td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{neuIds.has(r.id) && <span className="mr-1.5"><NeuEtikett klein /></span>}{datumDe(r.datum)}</td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{r.stueck ?? '—'}</td>
                  <td className="px-2.5 py-1.5 max-w-[260px]" title={r.was}>
                    <div className="truncate">
                      {r.was}
                      {!!(bilderIdx.data?.data as Record<string, number> | undefined)?.[String(r.id)] && (
                        <span className="ml-1.5 inline-flex items-center gap-0.5 text-slate-400 align-middle" title="Bild(er) vorhanden" data-testid={`bild-icon-${r.id}`}>
                          <Camera className="w-3.5 h-3.5" />{(bilderIdx.data?.data as Record<string, number>)[String(r.id)]}
                        </span>
                      )}
                    </div>
                  </td>
                  {zeigtKg && <td className="px-2.5 py-1.5 text-right">{r.kg ?? '—'}</td>}
                  {zeigtReferenz && <td className="px-2.5 py-1.5 max-w-[160px] truncate" title={r.referenz ?? ''}>{r.referenz ?? '—'}</td>}
                  <td className="px-2.5 py-1.5 max-w-[220px] truncate" title={r.kommentar ?? ''}>{r.kommentar ?? '—'}</td>
                  <td className="px-2.5 py-1.5 max-w-[160px] truncate" title={r.angefragt_bei ?? ''}>{r.angefragt_bei ?? '—'}</td>
                  <td className="px-2.5 py-1.5 max-w-[160px] truncate" title={r.bestellt_bei ?? ''}>{r.bestellt_bei ?? '—'}</td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{r.liefertermin ?? '—'}</td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{r.preis ?? '—'}</td>
                  <td className="px-2.5 py-1.5">{r.einheit ?? '—'}</td>
                  <td className="px-2.5 py-1.5 text-center">{r.ab ? '✓' : '—'}</td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{r.geliefert_am ? datumDe(r.geliefert_am) : '—'}</td>
                  <td className="px-2.5 py-1.5"><span className={`${CHIP} ${st.cls}`}>{st.label}</span></td>
                  <td className="px-1 py-1.5" onClick={e => e.stopPropagation()}>
                    <button aria-label={`Eintrag ${r.id} stornieren`} title="Stornieren" className="text-slate-300 hover:text-red-600 p-1"
                            onClick={() => setStorno({ id: r.id, titel: `Eintrag #${r.id}` })}><Ban className="w-4 h-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={schliesse}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8" onClick={e => e.stopPropagation()}
               onPaste={e => {   // Screenshot/Bild aus der Zwischenablage (Strg+V)
                 const bilder = Array.from(e.clipboardData?.files ?? []).filter(f => f.type.startsWith('image/'));
                 if (!bilder.length) return;
                 e.preventDefault();
                 if (modal.id) void api.einkaufSonstiges.bilderHochladen(modal.id, bilder).then(() => { bilderIdx.reload(); setBilderTick(t => t + 1); }).catch(err => setFehler((err as Error).message));
                 else setNeueBilder(l => [...l, ...bilder]);
               }}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
              <h2 className="font-semibold text-slate-900">
                {modal.id ? `Eintrag #${modal.id} bearbeiten` : 'Neuer Eintrag'} · {KATEGORIE_TABS.find(k => k.key === tab)?.label}
              </h2>
              <button onClick={schliesse} className="text-slate-400 hover:text-slate-600 text-xl leading-none" aria-label="Schließen">×</button>
            </div>
            <div className="px-6 py-4 space-y-3">
              {!modal.id && ent.wartet && ent.entwurf && (
                <EntwurfBanner at={ent.entwurf.at}
                  zusammenfassung={[ent.entwurf.data.stueck, ent.entwurf.data.was].filter(Boolean).join(' ') || undefined}
                  onWiederherstellen={() => { const d = ent.wiederherstellen(); if (d) setModal({ ...d }); }}
                  onVerwerfen={ent.verwerfen} />
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Feld label="Datum (wann)"><input className="input" type="date" value={modal.datum ?? ''} onChange={e => set('datum', e.target.value)} /></Feld>
                <Feld label="Stück"><input className="input" value={modal.stueck ?? ''} onChange={e => set('stueck', e.target.value)} /></Feld>
                <Feld label="Was *" span="md:col-span-2">
                  {modal.id
                    ? <input className="input" value={modal.was ?? ''} onChange={e => set('was', e.target.value)} autoFocus />
                    : <textarea className="input resize-none" rows={1} data-testid="was-feld" autoFocus value={modal.was ?? ''}
                                placeholder="Mehrere Zeilen einfügen → jede Zeile wird ein eigener Eintrag"
                                onChange={e => wasAendern(e.target.value)} />}
                </Feld>
                {zeigtKg && (
                  <Feld label="Kg"><input className="input" type="number" step="0.1" value={modal.kg ?? ''} onChange={e => set('kg', e.target.value === '' ? null : Number(e.target.value))} /></Feld>
                )}
                <Feld label="Referenz" span={zeigtKg ? '' : 'md:col-span-2'}><input className="input" placeholder="z. B. Fatih/Erkan, DV, Projekt" value={modal.referenz ?? ''} onChange={e => set('referenz', e.target.value)} /></Feld>
                <Feld label="Notiz (Sondersachen, z. B. Riffelblech)" span="md:col-span-2"><input className="input" placeholder="z. B. Riffelblech, Sonderlänge, besondere Güte …" value={modal.kommentar ?? ''} onChange={e => set('kommentar', e.target.value)} /></Feld>
                <Feld label="Angefragt bei"><input className="input" value={modal.angefragt_bei ?? ''} onChange={e => set('angefragt_bei', e.target.value)} /></Feld>
                <Feld label="Bestellt bei"><input className="input" value={modal.bestellt_bei ?? ''} onChange={e => set('bestellt_bei', e.target.value)} /></Feld>
                <Feld label="Liefertermin"><input className="input" value={modal.liefertermin ?? ''} onChange={e => set('liefertermin', e.target.value)} /></Feld>
                <Feld label="Preis"><input className="input" value={modal.preis ?? ''} onChange={e => set('preis', e.target.value)} /></Feld>
                <Feld label="Einheit"><input className="input" value={modal.einheit ?? ''} onChange={e => set('einheit', e.target.value)} /></Feld>
                <Feld label="AB"><input className="input" value={modal.ab ?? ''} onChange={e => set('ab', e.target.value)} /></Feld>
                <Feld label="Rechnungsnr."><input className="input" value={modal.rechnungsnummer ?? ''} onChange={e => set('rechnungsnummer', e.target.value)} /></Feld>
                {modal.id && <Feld label="Wareneingang (Datum)"><input className="input" type="date" value={modal.geliefert_am ?? ''} onChange={e => set('geliefert_am', e.target.value || null)} /></Feld>}
              </div>

              <BilderBlock id={modal.id} neu={neueBilder} onNeu={setNeueBilder} tick={bilderTick}
                           onGeaendert={() => { bilderIdx.reload(); }} onFehler={setFehler} />

              {!modal.id && (
                <div className="border-t border-slate-100 pt-3 space-y-2" data-testid="weitere-positionen">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Weitere Positionen (gleiche Angaben, eigener Eintrag)</div>
                  {weitere.map((w, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input className="input w-28" placeholder="Stück" aria-label={`Weitere Position ${i + 1} Stück`} value={w.stueck}
                             onChange={e => setWeitere(l => l.map((x, k) => (k === i ? { ...x, stueck: e.target.value } : x)))} />
                      <input className="input flex-1" placeholder="Was *" aria-label={`Weitere Position ${i + 1} Was`} value={w.was}
                             onChange={e => setWeitere(l => l.map((x, k) => (k === i ? { ...x, was: e.target.value } : x)))} />
                      {w.preis !== undefined && (
                        <>
                          <input className="input w-24" placeholder="Preis" aria-label={`Weitere Position ${i + 1} Preis`} value={w.preis}
                                 onChange={e => setWeitere(l => l.map((x, k) => (k === i ? { ...x, preis: e.target.value } : x)))} />
                          <input className="input w-20" placeholder="Einheit" aria-label={`Weitere Position ${i + 1} Einheit`} value={w.einheit ?? ''}
                                 onChange={e => setWeitere(l => l.map((x, k) => (k === i ? { ...x, einheit: e.target.value } : x)))} />
                        </>
                      )}
                      <button type="button" aria-label={`Weitere Position ${i + 1} entfernen`} className="text-slate-300 hover:text-red-600 text-lg px-1"
                              onClick={() => setWeitere(l => l.filter((_, k) => k !== i))}>×</button>
                    </div>
                  ))}
                  <button type="button" className="text-sm text-brand underline" data-testid="weitere-hinzu"
                          onClick={() => setWeitere(l => [...l, { stueck: '', was: '' }])}>＋ Weitere Position</button>
                  {tab === 'schweissen' && <div className="text-xs text-slate-500">Hinweis: Anfrage- und Bestelltexte enthalten automatisch „{ZEUGNIS_HINWEIS}“</div>}
                </div>
              )}

              {modal.id && (
                <div className="border-t border-slate-100 pt-3">
                  <button type="button" onClick={() => setVerlaufOffen(o => !o)} className="inline-flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-900">
                    {verlaufOffen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />} Verlauf
                  </button>
                  {verlaufOffen && (
                    <div className="mt-2">
                      <VerlaufListe laden={() => api.einkaufSonstiges.history(modal.id!)} labels={VERLAUF_LABEL} deps={[modal.id]} />
                    </div>
                  )}
                </div>
              )}
              {fehler && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{fehler}</div>}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between gap-3">
              <div>
                {modal.id && (
                  <button onClick={() => setStorno({ id: modal.id!, titel: `Eintrag #${modal.id}` })} disabled={busy}
                          className="inline-flex items-center gap-1.5 text-red-600 text-sm hover:underline disabled:opacity-50">
                    <Ban className="w-4 h-4" /> Stornieren
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={schliesse} className="btn-secondary">Abbrechen</button>
                <button onClick={() => void speichern()} disabled={busy || !modal.was?.trim()}
                        className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-40">
                  {busy ? 'Speichert …' : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {storno && <StornoDialog titel={storno.titel} onClose={() => setStorno(null)} onConfirm={stornieren} />}
      {listeOffen && (
        <ListeEinfuegenModal kategorie={tab} lieferanten={lieferanten} onClose={() => setListeOffen(false)} onFertig={() => list.reload()} />
      )}
      {aktion === 'storno' && (
        <StornoDialog titel={`${gewaehlt.length} Einträge`} onClose={() => setAktion(null)} onConfirm={sammelStornieren} />
      )}
      {aktion === 'anfrage' && (
        <AnfrageModal rows={fuerAnfrage} kategorie={tab} lieferanten={lieferanten}
                      onClose={() => setAktion(null)} onFertig={nachAktion} />
      )}
      {aktion === 'bestellen' && (
        <BestellModal rows={fuerBestellung} kategorie={tab} lieferanten={lieferanten}
                      onClose={() => setAktion(null)} onFertig={nachAktion} />
      )}

      {korb && (
        <PapierkorbModal
          titel={`stornierte Einträge · ${KATEGORIE_TABS.find(k => k.key === tab)?.label}`}
          laden={() => api.einkaufSonstiges.papierkorb(tab)}
          restore={id => api.einkaufSonstiges.restore(id)}
          darfWiederherstellen={darfWiederherstellen}
          zeile={r => <>#{r.id} · {r.stueck ? `${r.stueck} ` : ''}{r.was}{r.referenz ? <span className="ml-2 text-slate-500 font-normal">Ref. {r.referenz}</span> : null}</>}
          onClose={() => setKorb(false)}
          onRestored={() => list.reload()} />
      )}
    </>
  );
}

/* ───────────────────────── Gemeldet („Bedarf an Einkauf melden") ───────────────────────── */

type AStatus = EinkaufAufgabe['status'];
const ASTATUS_CHIP: Record<AStatus, { label: string; cls: string }> = {
  offen:    { label: 'Offen',    cls: 'bg-amber-100 text-amber-800' },
  bestellt: { label: 'Bestellt', cls: 'bg-sky-100 text-sky-800' },
  erledigt: { label: 'Erledigt', cls: 'bg-emerald-100 text-emerald-800' },
};
const AUFGABEN_LABEL: Record<string, string> = {
  titel: 'Was', notiz: 'Notiz', status: 'Status', prio: 'Priorität', typ: 'Typ',
  ansprechpartner: 'Ansprechpartner', angefordert_von: 'Angefordert von',
};

interface AufgabenListe {
  data: { data: EinkaufAufgabe[] } | null; loading: boolean; error: Error | null; reload: () => void;
}

function GemeldetListe({ liste, darfWiederherstellen }: { liste: AufgabenListe; darfWiederherstellen: boolean }) {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<'alle' | AStatus>('alle');
  const [modal, setModal] = useState<EinkaufAufgabe | null>(null);
  const [storno, setStorno] = useState<{ id: number; titel: string } | null>(null);
  const [korb, setKorb] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const [verlaufOffen, setVerlaufOffen] = useState(false);

  const alle = liste.data?.data ?? [];
  const suche = q.trim().toLowerCase();
  const rows = alle
    .filter(a => filter === 'alle' || a.status === filter)
    .filter(a => !suche || [a.titel, a.notiz, a.angefordert_von, a.erfasst_von, a.ansprechpartner].some(t => t?.toLowerCase().includes(suche)));
  const zaehl = (s: AStatus) => alle.filter(a => a.status === s).length;

  const set = <K extends keyof EinkaufAufgabe>(k: K, v: EinkaufAufgabe[K]) => setModal(m => (m ? { ...m, [k]: v } : m));

  const speichern = async () => {
    if (!modal?.titel.trim()) return;
    setBusy(true); setFehler('');
    try {
      await api.einkaufAufgaben.update(modal.id, {
        titel: modal.titel.trim(), notiz: modal.notiz ?? '', status: modal.status, prio: modal.prio,
        typ: modal.typ ?? 'bestellung', ansprechpartner: modal.ansprechpartner ?? '', angefordert_von: modal.angefordert_von ?? '',
      });
      setModal(null);
      liste.reload();
    } catch (e) {
      setFehler((e as Error).message);
      if ((e as { status?: number }).status === 404) liste.reload();
    }
    setBusy(false);
  };
  const stornieren = async (grund: string) => {
    if (!storno) return;
    await api.einkaufAufgaben.storno(storno.id, grund);
    setStorno(null); setModal(null); liste.reload();
  };

  return (
    <>
      <p className="text-sm text-slate-500 mb-3 max-w-3xl">
        Alles, was über <b>„Bedarf an Einkauf melden"</b> (Button oben rechts) hereinkommt. Erledigte Meldungen bleiben hier sichtbar —
        nur Stornieren (mit Grund) nimmt sie aus der Liste.
      </p>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <input placeholder="🔍 Suche …" value={q} onChange={e => setQ(e.target.value)} className="input min-w-[240px] max-w-[280px]" />
          <div className="flex gap-1.5 flex-wrap">
            <button className={FILTER(filter === 'alle')} onClick={() => setFilter('alle')}>Alle ({alle.length})</button>
            {(['offen', 'bestellt', 'erledigt'] as const).map(s => (
              <button key={s} className={FILTER(filter === s)} onClick={() => setFilter(s)}>{ASTATUS_CHIP[s].label} ({zaehl(s)})</button>
            ))}
          </div>
        </div>
        <button onClick={() => setKorb(true)} className="btn-secondary inline-flex items-center gap-1.5 whitespace-nowrap">
          <Trash2 className="w-4 h-4" /> Papierkorb
        </button>
      </div>

      {liste.error && <div className="mb-3"><ErrorBanner error={liste.error} onRetry={liste.reload} /></div>}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
        <table className="w-full text-[13px] min-w-[1000px]">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600 uppercase">
            <tr>
              {['Gemeldet am', 'Von', 'Was', 'Typ', 'Ansprechpartner', 'Notiz', 'Foto', 'Erledigt am', 'Status'].map(h => (
                <th key={h} className="text-left px-2.5 py-1.5 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {liste.loading && !liste.data && <tr><td colSpan={9} className="text-center py-10 text-slate-500">Lädt …</td></tr>}
            {liste.data && alle.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-slate-500">Nichts gemeldet.</td></tr>}
            {liste.data && alle.length > 0 && rows.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-slate-500">Keine Treffer</td></tr>
            )}
            {rows.map(a => {
              const st = ASTATUS_CHIP[a.status];
              return (
                <tr key={a.id} onClick={() => { setFehler(''); setVerlaufOffen(false); setModal({ ...a }); }} className="hover:bg-slate-50 cursor-pointer">
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{datumDe(a.created_at)}</td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{a.angefordert_von || a.erfasst_von || '—'}</td>
                  <td className="px-2.5 py-1.5 max-w-[320px]" title={a.titel}>
                    <div className="truncate">
                      {a.prio === 'eilig' && <span className={`${CHIP} bg-red-100 text-red-700 mr-1.5`}>EILIG</span>}
                      {a.titel}
                    </div>
                  </td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{a.typ === 'anfrage' ? 'Anfrage' : 'Bestellung'}</td>
                  <td className="px-2.5 py-1.5 max-w-[160px] truncate" title={a.ansprechpartner ?? ''}>{a.ansprechpartner ?? '—'}</td>
                  <td className="px-2.5 py-1.5 max-w-[260px] truncate" title={a.notiz ?? ''}>{a.notiz ?? '—'}</td>
                  <td className="px-2.5 py-1.5" onClick={e => e.stopPropagation()}>
                    {a.bild_key ? (
                      <a href={api.einkaufAufgaben.bildUrl(a.id)} target="_blank" rel="noreferrer" title="Foto ansehen">
                        <img src={api.einkaufAufgaben.bildUrl(a.id)} alt="" className="w-7 h-7 rounded object-cover border border-slate-200" />
                      </a>
                    ) : '—'}
                  </td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{datumDe(a.erledigt_at)}</td>
                  <td className="px-2.5 py-1.5"><span className={`${CHIP} ${st.cls}`}>{st.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
              <h2 className="font-semibold text-slate-900">Meldung #{modal.id}</h2>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600 text-xl leading-none" aria-label="Schließen">×</button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div className="text-xs text-slate-500">
                Gemeldet von <b>{modal.erfasst_von ?? '—'}</b> am {datumDe(modal.created_at)}
                {modal.erledigt_at ? <> · erledigt am {datumDe(modal.erledigt_at)}</> : null}
              </div>
              <div className="flex gap-1.5">
                {(['offen', 'bestellt', 'erledigt'] as const).map(s => (
                  <button key={s} type="button" onClick={() => set('status', s)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${modal.status === s ? `${ASTATUS_CHIP[s].cls} border-current` : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>
                    {ASTATUS_CHIP[s].label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Feld label="Was *" span="col-span-2"><input className="input" value={modal.titel} onChange={e => set('titel', e.target.value)} autoFocus /></Feld>
                <Feld label="Typ">
                  <select className="input" value={modal.typ ?? 'bestellung'} onChange={e => set('typ', e.target.value === 'anfrage' ? 'anfrage' : 'bestellung')}>
                    <option value="bestellung">Bestellung</option><option value="anfrage">Anfrage</option>
                  </select>
                </Feld>
                <Feld label="Priorität">
                  <select className="input" value={modal.prio} onChange={e => set('prio', e.target.value === 'eilig' ? 'eilig' : 'normal')}>
                    <option value="normal">Normal</option><option value="eilig">Eilig</option>
                  </select>
                </Feld>
                <Feld label="Angefordert von"><input className="input" value={modal.angefordert_von ?? ''} onChange={e => set('angefordert_von', e.target.value)} /></Feld>
                <Feld label="Ansprechpartner / Lieferant"><input className="input" value={modal.ansprechpartner ?? ''} onChange={e => set('ansprechpartner', e.target.value)} /></Feld>
                <Feld label="Notiz" span="col-span-2"><textarea className="input" rows={3} value={modal.notiz ?? ''} onChange={e => set('notiz', e.target.value)} /></Feld>
              </div>
              {modal.bild_key && (
                <a href={api.einkaufAufgaben.bildUrl(modal.id)} target="_blank" rel="noreferrer" className="inline-block">
                  <img src={api.einkaufAufgaben.bildUrl(modal.id)} alt="Foto zur Meldung" className="max-h-40 rounded border border-slate-200" />
                </a>
              )}
              <div className="border-t border-slate-100 pt-3">
                <button type="button" onClick={() => setVerlaufOffen(o => !o)} className="inline-flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-900">
                  {verlaufOffen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />} Verlauf
                </button>
                {verlaufOffen && (
                  <div className="mt-2">
                    <VerlaufListe laden={() => api.einkaufAufgaben.history(modal.id)} labels={AUFGABEN_LABEL} deps={[modal.id]} />
                  </div>
                )}
              </div>
              {fehler && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{fehler}</div>}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between gap-3">
              <button onClick={() => setStorno({ id: modal.id, titel: `Meldung #${modal.id}` })} disabled={busy}
                      className="inline-flex items-center gap-1.5 text-red-600 text-sm hover:underline disabled:opacity-50">
                <Ban className="w-4 h-4" /> Stornieren
              </button>
              <div className="flex gap-2">
                <button onClick={() => setModal(null)} className="btn-secondary">Abbrechen</button>
                <button onClick={() => void speichern()} disabled={busy || !modal.titel.trim()}
                        className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-40">
                  {busy ? 'Speichert …' : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {storno && <StornoDialog titel={storno.titel} onClose={() => setStorno(null)} onConfirm={stornieren} />}

      {korb && (
        <PapierkorbModal
          titel="stornierte Meldungen"
          laden={() => api.einkaufAufgaben.papierkorb()}
          restore={id => api.einkaufAufgaben.restore(id)}
          darfWiederherstellen={darfWiederherstellen}
          zeile={r => <>#{r.id} · {r.titel}<span className="ml-2 text-slate-500 font-normal">{ASTATUS_CHIP[r.status].label} · {r.erfasst_von ?? '—'}</span></>}
          onClose={() => setKorb(false)}
          onRestored={() => liste.reload()} />
      )}
    </>
  );
}

// 📷 Bilder zum Eintrag: bei neuem Eintrag vorgemerkt (Upload nach „Speichern“), bei bestehendem sofort in R2
function BilderBlock({ id, neu, onNeu, tick, onGeaendert, onFehler }: {
  id?: number; neu: File[]; onNeu: (f: File[]) => void; tick: number; onGeaendert: () => void; onFehler: (m: string) => void;
}) {
  const [liste, setListe] = useState<SonstigesBild[]>([]);
  const [busy, setBusy] = useState(false);
  const laden = () => { if (id) api.einkaufSonstiges.bilder(id).then(r => setListe(Array.isArray(r.data) ? r.data : [])).catch(() => setListe([])); };
  useEffect(() => { setListe([]); laden(); }, [id, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const [vorschau, setVorschau] = useState<string[]>([]);
  useEffect(() => {
    const urls = neu.map(f => (typeof URL.createObjectURL === 'function' ? URL.createObjectURL(f) : ''));
    setVorschau(urls);
    return () => { for (const u of urls) if (u && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(u); };
  }, [neu]);

  const hinzu = async (files: FileList | null) => {
    const bilder = Array.from(files ?? []).filter(f => f.type.startsWith('image/'));
    if (!bilder.length) return;
    if (!id) { onNeu([...neu, ...bilder]); return; }
    setBusy(true);
    try { await api.einkaufSonstiges.bilderHochladen(id, bilder); laden(); onGeaendert(); }
    catch (e) { onFehler((e as Error).message); }
    setBusy(false);
  };
  const loeschen = async (name: string) => {
    if (!id || !confirm('Bild löschen?')) return;
    setBusy(true);
    try { await api.einkaufSonstiges.bildLoeschen(id, name); laden(); onGeaendert(); }
    catch (e) { onFehler((e as Error).message); }
    setBusy(false);
  };
  return (
    <div className="border-t border-slate-100 pt-3" data-testid="bilder-block">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Bilder</div>
      <div className="flex flex-wrap gap-2 items-center">
        {liste.map(b => (
          <div key={b.name} className="relative group">
            <a href={api.einkaufSonstiges.bildUrl(id!, b.name)} target="_blank" rel="noreferrer" title={b.name}>
              <img src={api.einkaufSonstiges.bildUrl(id!, b.name)} alt="Bild zum Eintrag" className="w-20 h-20 rounded object-cover border border-slate-200" />
            </a>
            <button type="button" aria-label="Bild löschen" onClick={() => void loeschen(b.name)} disabled={busy}
                    className="absolute -top-1.5 -right-1.5 bg-white border border-slate-300 rounded-full w-5 h-5 text-xs leading-none text-slate-500 hover:text-red-600">×</button>
          </div>
        ))}
        {neu.map((f, i) => (
          <div key={i} className="relative" data-testid="bild-vorgemerkt">
            <img src={vorschau[i] ?? ''} alt={f.name} title={f.name} className="w-20 h-20 rounded object-cover border border-dashed border-brand" />
            <button type="button" aria-label="Bild entfernen" onClick={() => onNeu(neu.filter((_, k) => k !== i))}
                    className="absolute -top-1.5 -right-1.5 bg-white border border-slate-300 rounded-full w-5 h-5 text-xs leading-none text-slate-500 hover:text-red-600">×</button>
          </div>
        ))}
        <label className={`inline-flex items-center gap-1.5 text-sm text-brand hover:underline cursor-pointer ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
          <input type="file" accept="image/*" multiple className="hidden" data-testid="bild-input"
                 onChange={e => { void hinzu(e.target.files); e.target.value = ''; }} />
          <Camera className="w-4 h-4" /> {busy ? 'Lädt …' : 'Bild hinzufügen'}
        </label>
        <span className="text-xs text-slate-400">oder Bild mit Strg+V einfügen</span>
      </div>
    </div>
  );
}

function Feld({ label, span = '', children }: { label: string; span?: string; children: React.ReactNode }) {
  return (
    <div className={span}>
      <label className="block text-xs text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
